/**
 * Entra ID sign-in.
 *
 * Single-tenant: only Westwood accounts can sign in, enforced by the authority
 * URL and again by the API when it validates the token issuer.
 *
 * Tokens are cached in sessionStorage rather than localStorage - a shared or
 * kiosk browser should not keep someone signed in after the tab closes, and
 * project documents are the kind of thing that matters.
 */

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID;
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;

/** The scope the API expects in the token audience. */
export const API_SCOPE = import.meta.env.VITE_API_SCOPE;

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
});

let initialised = null;

/** MSAL v3 must be initialised before any other call. */
export function initMsal() {
  if (!initialised) {
    initialised = msalInstance
      .initialize()
      .then(() => msalInstance.handleRedirectPromise())
      .then((result) => {
        const account = result?.account || msalInstance.getAllAccounts()[0];
        if (account) msalInstance.setActiveAccount(account);
        return account || null;
      });
  }
  return initialised;
}

export function getAccount() {
  return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
}

export function login(returnTo = window.location.href) {
  return msalInstance.loginRedirect({
    scopes: [API_SCOPE],
    state: returnTo,
  });
}

export function logout(returnTo = window.location.origin) {
  return msalInstance.logoutRedirect({ postLogoutRedirectUri: returnTo });
}

/**
 * Get an access token for the API.
 *
 * Silent first. If the refresh token has expired or consent is missing, MSAL
 * throws InteractionRequiredAuthError and the only way forward is a redirect,
 * so we start one rather than surfacing a failure the user cannot act on.
 */
export async function getAccessToken() {
  await initMsal();
  const account = getAccount();
  if (!account) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes: [API_SCOPE], account });
      return null;
    }
    throw error;
  }
}
